import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { companyService } from '../../services/api';
import { Company } from '../../types';
import { toast } from 'react-toastify';

const Settings: React.FC = () => {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    ice: '',
    if_number: '',
    cnss: '',
    rc: '',
    address: '',
    email: '',
    phone: '',
    fax: '',
    header_template: '',
    footer_template: '',
  });

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      setLoading(true);
      const response = await companyService.getSettings();
      const companyData = response.data;
      setCompany(companyData);
      setFormData({
        name: companyData.name || '',
        ice: companyData.ice || '',
        if_number: companyData.if_number || '',
        cnss: companyData.cnss || '',
        rc: companyData.rc || '',
        address: companyData.address || '',
        email: companyData.email || '',
        phone: companyData.phone || '',
        fax: companyData.fax || '',
        header_template: companyData.header_template || '',
        footer_template: companyData.footer_template || '',
      });
    } catch (error) {
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      await companyService.updateSettings(formData);
      toast.success('Paramètres sauvegardés avec succès');
      fetchCompanySettings();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center">Chargement...</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Paramètres de l'entreprise</h1>
      </div>

      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>
            <h5>Informations générales</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Raison sociale *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Téléphone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fax</Form.Label>
                  <Form.Control
                    type="text"
                    name="fax"
                    value={formData.fax}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Adresse</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h5>Informations fiscales</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ICE</Form.Label>
                  <Form.Control
                    type="text"
                    name="ice"
                    value={formData.ice}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>IF</Form.Label>
                  <Form.Control
                    type="text"
                    name="if"
                    value={formData.if_number}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>CNSS</Form.Label>
                  <Form.Control
                    type="text"
                    name="cnss"
                    value={formData.cnss}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>RC</Form.Label>
                  <Form.Control
                    type="text"
                    name="rc"
                    value={formData.rc}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h5>Personnalisation des documents</h5>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>En-tête personnalisé</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="header_template"
                value={formData.header_template}
                onChange={handleInputChange}
                placeholder="Texte qui apparaîtra en haut de vos documents..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Pied de page personnalisé</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="footer_template"
                value={formData.footer_template}
                onChange={handleInputChange}
                placeholder="Texte qui apparaîtra en bas de vos documents..."
              />
            </Form.Group>

            <Alert variant="info">
              <small>
                Ces textes apparaîtront sur tous vos documents PDF (devis, factures, bons de livraison, etc.)
              </small>
            </Alert>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-end">
          <Button 
            type="submit" 
            variant="primary" 
            disabled={saving}
            size="lg"
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder les paramètres'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default Settings;